import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout"
import React, { useEffect, useState } from "react"
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { toggleDetailTab } from "../../../app/slices/VerificationModuleSlice";
import { useCaseFindQuery } from "../../../services/scheduleService/schedule-api";
import { useVerificationFindQuery } from "../../../services/verifyService/verify-api";
import { Authorization, Benefits, BenefitsAttributes, BenefitsStatus, CaseIndicators, Estimate, FinancialOrientation, Header, Insurances, Notes, PatientOutOfPocket, SingleCaseAgreement, VerifyDocuments } from "../features"
import { CallInfo } from "../features/detail/benefits/CallInfo"
import { Loading } from "../../shared/features/Loading"
import {Verification as Verify} from '../../../services/verifyService/model';
import { AuthorizationStatus } from "../features/detail/authorizationStatus/AuthorizationStatus"
import { Grid, Stack, Table, Alert } from "@mui/material";
import { CenterNameChipView } from "../../../components/orgComponents/CenterNameChipView";
import { PhysicianChipView } from "../../../components/encounterItems";
import { CommandBar } from "../features/detail/commandBar/CommandBar";
import { useMyProfileQuery } from '../../../services/profileService/profile-api';
import { PreServiceCodingReview } from '../features/detail/estimate/PreServiceCodingReveiw';
import { SelfPayReviewComplete } from '../features/detail/estimate/SelfPayReviewComplete';
import { usePayerDetailsQuery } from '../../../services/orgService/org-api';
import { PayerForCenterRequest } from '../../../services/orgService/commands';
import { RequiresBcbsT2GAuth } from '../specs/RequiresBcbsT2GAuth';

function Verification(){
    const dispatch = useAppDispatch();
    const {id} = useParams();
    const tabIndex = useAppSelector((state)=> state.verificationModule.DetailTab);
    const verify = useAppSelector((state) => state.verification.verification);
    const toggleTab = (e: TabStripSelectEventArguments) => dispatch(toggleDetailTab(e.selected));
    const schedule = useCaseFindQuery(id!);
    const {data, isLoading} = useVerificationFindQuery(id!);
    const [verification, setVerification] = useState(new Verify());
    const { data: myProfile } = useMyProfileQuery();
    const primaryPayer = schedule.data?.insurances?.find(p => p.priority === 1);
    const calctype = usePayerDetailsQuery(new PayerForCenterRequest(verify.center, primaryPayer?.payerCode ?? '')).data?.financialClass;
    const isBcbsT2G = primaryPayer && calctype?.includes('BCBS') && new RequiresBcbsT2GAuth().IsSatisfiedBy(primaryPayer);
    useEffect(() => {setVerification(data!)});
    const handleChange = (e:any) =>{
        const {name, value} = e.target;
        setVerification({...verification,[name]:value});
    }

    return (
        <React.Fragment>
            {(verify?.verifyAttributes?.isWelM2 === true) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    WELM2 benefits detected for this case.
                </Alert>
            )}
            {(schedule.data?.insurances?.some(i => i.payerCode === 'CB')) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Scheduled as Commercial Payer, please update the EMR.
                </Alert>
            )}
            {(schedule.data?.insurances?.some(i => i.payerCode === 'WB' || i.payerCode === 'W')) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Scheduled as Workers Comp, please update the EMR.
                </Alert>
            )}
            {(schedule.isLoading || isLoading) ? <Loading/> :
                <div>
                    <Grid container>
                        <Grid item sm={4.75}><CenterNameChipView centerId={schedule.data?.center ?? verify?.center ?? ''}/></Grid>
                        <Grid item sm={5}><PhysicianChipView/></Grid>
                        <Grid item sm={2.25}justifyContent='flex-end'><CommandBar/></Grid>
                    </Grid>
                    <Header centerId="IEB" visitId="1"/>
                    <Insurances centerId="IEB" visitId="1"/>
                    <CaseIndicators/>
                    <TabStrip selected={tabIndex} onSelect={toggleTab}>
                        <TabStripTab title='Coverage'>
                            {isBcbsT2G &&
                            <Alert severity="error" sx={{ mb: 2 }}>BCBS T2G plan detected — PCP referral and facility authorization are required.</Alert>
                            }
                            <Table/>
                            <Grid container marginLeft={'10px'} marginRight={'20px'}>
                                <Grid item sm={8}><Benefits/><hr/><CallInfo/></Grid>
                                <Grid item sm={4}><BenefitsStatus/><BenefitsAttributes/></Grid>
                            </Grid>
                        </TabStripTab>
                        <TabStripTab title='Estimate'>
                            <Table/>
                            <Grid container>
                                <Grid item sm={12}><Estimate/></Grid>
                            </Grid>
                        </TabStripTab>
                        <TabStripTab title='Authorization'>
                            <Table/>
                            <Grid container marginTop={'5px'} marginLeft={'10px'}>
                                <Grid item sm={8}><Authorization/></Grid>
                                <Grid item sm={4} ><AuthorizationStatus/></Grid>
                            </Grid>
                        </TabStripTab>
                        <TabStripTab title='Financial Orientation'>
                            <Table/>
                            <FinancialOrientation/>
                        </TabStripTab>
                        <TabStripTab title='Notes'>
                            <Table/>
                            <Notes/>
                        </TabStripTab>
                        <TabStripTab title='Documents'>
                            <Table/>
                            <VerifyDocuments/>
                        </TabStripTab>
                        {((myProfile?.role || '').toLowerCase() === 'admin' || (myProfile?.role || '') === 'RevCycle Admin') &&
                            <TabStripTab title='Management'>
                                <div style={{padding: 24, fontSize: 18, color: '#666'}}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Review Completion</div>
                                    <PreServiceCodingReview />
                                    <SelfPayReviewComplete />
                                </div>
                            </TabStripTab>
                        }
                        {verify?.singleCaseAgreement?.agreementRequired === true &&
                        <TabStripTab title='Single Case Agreement'>
                            <Table/>
                            <SingleCaseAgreement/>
                        </TabStripTab>
                        }
                    </TabStrip>
                </div>
            }
        </React.Fragment>
    );
}

export {Verification}